/*
 * @format
 *
 * This is a generator for a reducer that follows the pending state pattern we've prototyped.
 * Reported state is stored separately.
 * Users desired changes are stored here when the user makes a change.
 * The selector picks the pending state, if that doesn't exist it uses the real one
 *
 * When we receive an rejection or timeout
 * - We add an error to the array (and the ui will pop a modal)
 * - The pending value is removed
 *
 * When we receive a completion
 * - update the request id to 'done' so other updates won't match
 * - but it can be read by the selectors
 *
 * When we get a Live message
 * - Clear out requests that are 'done'.
 *
 */

import * as actionTypes from '../action-types';

/*
const examle = {
  requests: [
    {
      zoneDeviceId: 'prt-123',
      endpointId: 0,
      requestId: 'abcd-efgh-1234-5678',
      request: {
        setpoint: 2000,
      },
    },
  ],
  errors: [
    {
      zoneDeviceId: 'prt-123',
      endpointId: 0,
      requestId: 'abcd-efgh-1234-5678',
      message: 'There was an error',
    },
  ],
};
*/

export const initialState = {
  requests: [],
  errors: [],
};

const matchingHomeId = (request, homeId) => request.homeId === homeId;

const matchingZoneId = (request, zoneDeviceId, endpointId) =>
  request.zoneDeviceId === zoneDeviceId && request.endpointId === endpointId;

export const pendingGenerator =
  (
    startType,
    rejectedType,
    completedType,
    errorLabel,
    clearType = actionTypes.WEBSOCKET_LIVE_RECEIVED,
  ) =>
  (state = initialState, action) => {
    // split the type value off
    const { type, ...actionData } = action;

    switch (type) {
      case startType: {
        // split manditory and data values. sendWsMessage is the live websocket
        // sender that SET_SCHEDULE_STARTED carries for the saga — transport, not
        // state, and a function in state is not serializable, so it is dropped
        // rather than spread into the stored request.
        const { homeId, zoneId, requestId, sendWsMessage, ...data } = actionData;

        if (!zoneId) {
          return {
            ...state,
            requests: [
              // remove existing requests for that device
              ...state.requests.filter(
                request => !matchingHomeId(request, homeId),
              ),
              // add a new one
              {
                homeId,
                requestId,
                ...data,
              },
            ],
            errors: [],
          };
        }

        return {
          ...state,
          requests: [
            // remove existing requests for that device
            ...state.requests.filter(
              request => !matchingZoneId(request, zoneId[0], zoneId[1]),
            ),
            // add a new one
            {
              homeId,
              zoneDeviceId: zoneId[0],
              endpointId: zoneId[1],
              requestId,
              ...data,
            },
          ],
          errors: [],
        };
      }

      case completedType: {
        const requestId = actionData.requestId || actionData.data.requestId;

        // remove entry if it exists
        const entry = state.requests.find(r => r.requestId === requestId);

        if (entry) {
          return {
            ...state,
            requests: [
              ...state.requests.filter(
                request => request.requestId !== requestId,
              ),
              {
                ...entry,
                requestId: 'done',
              },
            ],
          };
        }
        break;
      }

      case rejectedType:
      case actionTypes.COMMAND_STATUS_NACK:
      case actionTypes.PENDING_CHANGE_TIMED_OUT: {
        // find request,
        // remove it,
        // if it was removed, add an error
        const requestId = actionData.requestId || actionData.data.requestId;

        const entry = state.requests.find(
          request => request.requestId === requestId,
        );
        if (entry) {
          const message =
            type === rejectedType
              ? `${errorLabel} rejected`
              : type === actionTypes.PENDING_CHANGE_TIMED_OUT
              ? `${errorLabel} timed out`
              : `Unable to make ${errorLabel} change`;

          const errors = [...state.errors];

          // always show the rejection error, not the timeout
          if (
            errorLabel &&
            (type === rejectedType || !actionData?.suppressTimeoutError)
          ) {
            errors.push({
              requestId,
              type:
                type === rejectedType ? 'commandRejected' : 'commandTimeout',
              raised: Date.now(),
              homeId: entry.homeId,
              zoneDeviceId: entry.zoneDeviceId,
              endpointId: entry.endpointId,
              message,
            });
          }

          return {
            ...state,
            requests: [
              // keep requests that don't match
              ...state.requests.filter(
                request => request.requestId !== requestId,
              ),
            ],
            errors,
          };
        }
        break;
      }

      case actionTypes.PENDING_CHANGE_SUPERSEDED: {
        // A change THIS client made was replaced on the server before it
        // landed. Remove the local pending entry silently — no error is
        // surfaced — because the replacing goal arrives in the same
        // desired-state update. Also drop any error already raised for this
        // requestId: a superseded can arrive AFTER the client-side command
        // timeout has pushed a timeout error, and that stale error must not
        // linger once the server has told us the request was superseded (the
        // local entry is already gone from requests[] by then, so match on
        // errors[] too). Placed after the reject/nack/timeout block so
        // supersession never takes the error path those terminals use, and
        // before the clear so a superseded entry can never be mistaken for a
        // completed 'done' one.
        const requestId = actionData.requestId || actionData.data?.requestId;

        const hasRequest = state.requests.some(
          request => request.requestId === requestId,
        );
        const hasError = state.errors.some(
          error => error.requestId === requestId,
        );

        if (hasRequest || hasError) {
          return {
            ...state,
            requests: state.requests.filter(
              request => request.requestId !== requestId,
            ),
            errors: state.errors.filter(
              error => error.requestId !== requestId,
            ),
          };
        }
        break;
      }

      case actionTypes.PENDING_CHANGE_FAILED: {
        // A change THIS client made failed terminally with no replacing goal.
        // Drop the local optimistic entry so the render reverts to reported,
        // rather than keep showing the value that never took. Errors are left
        // untouched: the failure is surfaced through the notification path, not
        // by synthesising a pending-slice error here. Placed after supersession
        // so a failure never takes the reject/timeout error path.
        const requestId = actionData.requestId || actionData.data?.requestId;

        if (state.requests.some(request => request.requestId === requestId)) {
          return {
            ...state,
            requests: state.requests.filter(
              request => request.requestId !== requestId,
            ),
          };
        }
        break;
      }

      case clearType: {
        // match on requestId then by homeId, other just wipe all if nothing provided
        if (action.requestId) {
          return {
            ...state,
            requests: [
              ...state.requests.filter(
                r => !(r.requestId === action.requestId),
              ),
            ],
          };
        } else {
          if (actionData.homeId) {
            return {
              ...state,
              requests: [
                ...state.requests.filter(
                  r => !(r.homeId === actionData.homeId),
                ),
              ],
            };
          } else {
            // otherwise remove all done states
            return {
              ...state,
              requests: [...state.requests.filter(r => r.requestId !== 'done')],
            };
          }
        }
      }

      case actionTypes.DISMISS_ERROR: {
        const { requestId } = actionData;
        return {
          ...state,
          errors: state.errors.filter(
            request => request.requestId !== requestId,
          ),
        };
      }

      // wipe everything on logout so pending/'done' requests can't leak into
      // the next session (e.g. exiting and re-entering demo mode)
      case actionTypes.LOGOUT_COMPLETED: {
        return initialState;
      }

      default:
        break;
    }

    return state;
  };

export const getPendingData = (state, homeId, zoneId) => {
  if (!state || !state.requests) {
    return undefined;
  }

  return state.requests.find(
    r =>
      r.homeId === homeId &&
      r.zoneDeviceId === zoneId[0] &&
      r.endpointId === zoneId[1],
  );
};

// True when the slice currently holds a request with this id (any status,
// including a completed 'done' entry). Used to recognise a goal echoing one of
// this client's own commands.
export const hasPendingRequestId = (state, requestId) =>
  !!state &&
  Array.isArray(state.requests) &&
  state.requests.some(request => request.requestId === requestId);

export const getPendingError = state => state.errors[0];

export const pendingSetpoint = pendingGenerator(
  actionTypes.SET_SETPOINT_STARTED,
  actionTypes.WEBSOCKET_SET_SETPOINT_REJECTED,
  actionTypes.WEBSOCKET_SET_SETPOINT_RECEIVED,
  undefined, // 'Setpoint',
  actionTypes.CLEAR_PENDING_STATE,
);

export const pendingMode = pendingGenerator(
  actionTypes.SET_MODE_STARTED,
  actionTypes.WEBSOCKET_SET_PRT_MODE_REJECTED,
  actionTypes.WEBSOCKET_SET_PRT_MODE_RECEIVED,
  undefined, // 'Mode change',
  actionTypes.CLEAR_PENDING_STATE,
);

export const pendingSchedule = pendingGenerator(
  actionTypes.SET_SCHEDULE_STARTED,
  actionTypes.SET_SCHEDULE_TIMED_OUT,
  actionTypes.SET_SCHEDULE_CONFIRMED, // Mark as 'done' on confirmation so loading stops
  undefined,
  actionTypes.CLEAR_PENDING_SCHEDULE,
);

export const pendingHolidayMode = pendingGenerator(
  actionTypes.SET_HOLIDAY_MODE_STARTED,
  actionTypes.WEBSOCKET_SET_HOLIDAY_MODE_REJECTED,
  actionTypes.WEBSOCKET_SET_HOLIDAY_MODE_RECEIVED,
  'Holiday mode',
  actionTypes.CLEAR_PENDING_STATE,
);

export const pendingPublish = pendingGenerator(
  actionTypes.WEBSOCKET_PUBLISH_STARTED,
  actionTypes.WEBSOCKET_PUBLISH_REJECTED,
  actionTypes.WEBSOCKET_PUBLISH_RECEIVED,
  'Publish',
);

export default {
  pendingSetpoint,
  pendingMode,
  pendingHolidayMode,
  pendingPublish,
};
